import { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTracking } from './useTracking';
import { useFavoritesContext } from '../contexts/FavoritesCountContext';
import { logAnalyticsStartupEvent } from './analytics';

interface AddToFavoritesOptions {
  source?: 'catalog' | 'match' | 'search' | 'recommendation' | 'favorites';
  category?: string;
  country?: string;
  industry?: string;
}

interface FavoriteResponse {
  success: boolean;
  favoriteId?: string;
  message?: string;
  error?: string;
  alreadyFavorited?: boolean;
  investorStatus?: string;
}

interface RemoveFavoriteResponse {
  success: boolean;
  message?: string;
  error?: string;
}

type StartupDisclosureConsent = 'granted' | 'denied';

interface StartupDisclosureConsentResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Hook personalizado para gestionar favoritos
 *
 * @example
 * const { addToFavorites, removeFromFavorites, isLoading, error } = useFavorites();
 *
 * // Agregar a favoritos
 * const result = await addToFavorites('startup-id-123', {
 *   source: 'catalog',
 *   category: 'Your Matches'
 * });
 *
 * // Remover de favoritos
 * await removeFromFavorites('startup-id-123');
 */
export const useFavorites = () => {
  const functions = getFunctions();
  const { trackFavorite } = useTracking();
  const {
    isFavorited: contextIsFavorited,
    favoritesReady,
    invalidateFavorites,
  } = useFavoritesContext();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Agregar una startup a favoritos
   * También trackea la acción automáticamente
   */
  const addToFavorites = useCallback(
    async (startupId: string, options?: AddToFavoritesOptions): Promise<FavoriteResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const addToFav = httpsCallable(functions, 'addToFavorites');
        const result = await addToFav({
          startupId,
          context: {
            source: options?.source,
            category: options?.category,
          },
        });

        const data = result.data as FavoriteResponse;

        // Track la acción solo si fue exitosa
        if (data.success) {
          trackFavorite(startupId, {
            source: options?.source,
            category: options?.category,
          });
          void logAnalyticsStartupEvent('favorite_startup', {
            startupId,
            country: options?.country,
            industry: options?.industry,
            source: options?.source,
          });
          invalidateFavorites();
        }

        setIsLoading(false);
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Error al agregar a favoritos';
        setError(errorMessage);
        setIsLoading(false);
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [functions, trackFavorite, invalidateFavorites]
  );

  /**
   * Remover una startup de favoritos
   */
  const removeFromFavorites = useCallback(
    async (startupId: string): Promise<RemoveFavoriteResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const removeFromFav = httpsCallable(functions, 'removeFromFavorites');
        const result = await removeFromFav({
          startupId,
        });

        const data = result.data as RemoveFavoriteResponse;

        if (data.success) {
          invalidateFavorites();
        }

        setIsLoading(false);
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Error al remover de favoritos';
        setError(errorMessage);
        setIsLoading(false);
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [functions, invalidateFavorites]
  );

  /**
   * Verificar si una startup está en favoritos
   * Carga la lista completa de favoritos y verifica si el startupId está incluido
   */
  const checkIsFavorited = useCallback(
    async (startupId: string): Promise<boolean> => {
      if (favoritesReady) {
        return contextIsFavorited(startupId);
      }
      try {
        const getFavorites = httpsCallable(functions, 'getFavorites');
        const result = await getFavorites({});
        const data = result.data as { success: boolean; favorites?: any[] };
        if (data.success && data.favorites) {
          return data.favorites.some((fav: any) => fav.id === startupId);
        }
        return false;
      } catch (err) {
        console.error('Error checking favorite status:', err);
        return false;
      }
    },
    [functions, favoritesReady, contextIsFavorited]
  );

  const setStartupDisclosureConsent = useCallback(
    async (
      startupId: string,
      consent: StartupDisclosureConsent
    ): Promise<StartupDisclosureConsentResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const setConsent = httpsCallable(functions, 'setStartupDisclosureConsent');
        // console.log('[favorite-consent] calling setStartupDisclosureConsent', {
        //   startupId,
        //   consent,
        // });
        const result = await setConsent({
          startupId,
          consent,
        });
        const data = result.data as StartupDisclosureConsentResponse;
        // console.log('[favorite-consent] callable response', data);
        setIsLoading(false);
        return data;
      } catch (err: any) {
        const errorMessage = err.message || 'Error saving consent preference';
        console.error('[favorite-consent] callable error', err);
        setError(errorMessage);
        setIsLoading(false);
        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [functions]
  );

  /**
   * Limpiar el error actual
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    addToFavorites,
    removeFromFavorites,
    checkIsFavorited,
    setStartupDisclosureConsent,
    isLoading,
    error,
    clearError,
  };
};
